import OpenWithIcon from "@mui/icons-material/OpenWith";
import RotateLeftRoundedIcon from "@mui/icons-material/Rotate90DegreesCcwRounded";
import RotateRightRoundedIcon from "@mui/icons-material/Rotate90DegreesCwRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import ZoomOutRoundedIcon from "@mui/icons-material/ZoomOutRounded";
import { Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import { FC, useEffect, useState } from "react";
import Cropper, { Area, Point } from "react-easy-crop";

import {
  ControlContainer,
  ControlIcon,
  ControlIconButton,
  CropContainer,
  CropSlider,
  GappedContainer,
  LabelText,
  MoveBtn,
  ZoomSlider,
} from "./ImageCropper.styles";
import { ImageCropperProps } from "./ImageUploadModal.types";

const ImageCropper: FC<ImageCropperProps> = ({ uploadedImage, setImageProperties }) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>({
    height: 0,
    width: 0,
    x: 0,
    y: 0,
  });

  useEffect(() => {
    setImageProperties((prevVal) => {
      if (
        prevVal.zoom !== zoom ||
        prevVal.rotation !== rotation ||
        prevVal.croppedAreaPixels !== croppedAreaPixels
      ) {
        return { zoom, rotation, croppedAreaPixels };
      }
      return prevVal;
    });
  }, [croppedAreaPixels, rotation, setImageProperties, zoom]);

  const onCropComplete = (_croppedArea, croppedAreaPixels: Area): void => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleZoomClick = (isZoomIn = true) => {
    if (isZoomIn) {
      setZoom((prev) => (prev >= 3 ? prev : prev + 0.1));
    } else {
      setZoom((prev) => (prev <= 1 ? prev : prev - 0.1));
    }
  };

  const handleRotationChange = (isLeft = true) => {
    if (rotation >= 360 || rotation <= -360) {
      setRotation(0);
    }
    if (isLeft) {
      setRotation((prev) => prev - 90);
    } else {
      setRotation((prev) => prev + 90);
    }
  };

  return (
    <>
      <CropContainer>
        <Cropper
          image={uploadedImage}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={1}
          cropShape="rect"
          showGrid={false}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          onRotationChange={setRotation}
        />
        <MoveBtn className="move-btn">
          <OpenWithIcon /> <label htmlFor="none">Move</label>
        </MoveBtn>
      </CropContainer>

      <ControlContainer>
        <GappedContainer>
          <Tooltip title="Zoom Out">
            <ControlIconButton onClick={() => handleZoomClick(false)}>
              <ControlIcon as={ZoomOutRoundedIcon} />
            </ControlIconButton>
          </Tooltip>
          <LabelText>
            <p>Zoom</p>
          </LabelText>
          <Tooltip title="Zoom In">
            <ControlIconButton onClick={() => handleZoomClick()}>
              <ControlIcon as={ZoomInRoundedIcon} />
            </ControlIconButton>
          </Tooltip>
        </GappedContainer>

        <ZoomSlider>
          <CropSlider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            aria-labelledby="Zoom"
            onChange={(_e, value) => setZoom(value as number)}
            title="Slide to zoom in or out"
          />
        </ZoomSlider>

        <GappedContainer>
          <Tooltip title="Rotate Left">
            <ControlIconButton onClick={() => handleRotationChange()}>
              <ControlIcon as={RotateLeftRoundedIcon} />
            </ControlIconButton>
          </Tooltip>
          <LabelText>
            <p>Rotate</p>
          </LabelText>
          <Tooltip title="Rotate Right">
            <ControlIconButton onClick={() => handleRotationChange(false)}>
              <ControlIcon as={RotateRightRoundedIcon} />
            </ControlIconButton>
          </Tooltip>
        </GappedContainer>
      </ControlContainer>
    </>
  );
};

ImageCropper.propTypes = {
  uploadedImage: PropTypes.string.isRequired,
  setImageProperties: PropTypes.func.isRequired,
};

export default ImageCropper;
